import React, { useMemo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';

/**
 * @prop isDisabled:
 * When the JSM portal request form is submitted, all the fields get disabled.
 * Because of the nature of apps, we can't control what's happening inside nor we have existing API to communicate to apps that they have to disable fields.
 * On top of that, different experiences handle forms differently, until now none of them were disabling fields during the submission, only JSM does it.
 * This component is a wrapper that would disable pointer events for all the children coming from Forge apps so when user clicks on the field, it won't get a focus.
 * However, they might still navigate to the field using keyboard (Tab key).
 * This is not a perfect solution but it's the best we can do for now and considering the submission happens fast, there is not need to complicate this.
 *
 * @prop isVisible:
 * We need to hide the field from UI while it is loading in the background.
 * The parent component will show skeleton meanwhile and will toggle the prop of this component once the app has been loaded.
 */
export const FieldState = ({
    isDisabled,
    isVisible,
    children,
}: {
    isDisabled: boolean;
    isVisible: boolean;
    children: React.ReactNode;
}) => {
    const styles = useMemo(
        () => [isVisible ? undefined : hiddenStyles, isDisabled ? disabledStyles : undefined].filter(Boolean),
        [isVisible, isDisabled]
    );

    return <Box xcss={styles}>{children}</Box>;
};

const hiddenStyles = xcss({
    display: 'none',
});

const disabledStyles = xcss({
    opacity: 0.4,
    // @ts-expect-error we want to disable pointer events for all the children coming from Forge apps
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '*': {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
        pointerEvents: 'none !important',
    },
});
